import { JsonObject, JsonProperty } from 'json2typescript';
import { Moment } from 'moment';
import * as uuid from 'uuid';

import { BooleanInverterAdapter } from '../../client/helpers/serializer/adapters/boolean-inverter.adapter';
import { DocumentType, Nature, PBCInterface, ShareHolderInterface, ShareHolderType } from '../../../core';
import { DocumentTypeAdapter } from '../../client/helpers/serializer/adapters/document-type.adapter';
import { Id } from './id';
import { NatureAdapter } from '../../client/helpers/serializer/adapters/nature.adapter';
import { PBC } from './pbc';
import { ShareHolderTypeAdapter } from '../../client/helpers/serializer/adapters/share-holder-type.adapter';
import { StringAdapter } from '../../client/helpers/serializer/adapters/string.adapter';

@JsonObject('ShareHolder')
export class ShareHolder implements ShareHolderInterface {
    private readonly _id: Id = undefined;

    @JsonProperty('ShareholderId', Number, true)
    private _shareholderId: number = undefined;

    @JsonProperty('Type', ShareHolderTypeAdapter, true)
    private _type: ShareHolderType = undefined;

    @JsonProperty('Name', StringAdapter, true)
    private _name: string = undefined;

    @JsonProperty('Surname', StringAdapter, true)
    private _surname: string = undefined;

    @JsonProperty('DocType', DocumentTypeAdapter, true)
    private _docType: DocumentType = undefined;

    @JsonProperty('IdNumber', StringAdapter, true)
    private _docId: string = undefined;

    @JsonProperty('Nationality', StringAdapter, true)
    private _citizenship: string = undefined;

    @JsonProperty('OwnershipPercentaje', Number, true)
    private _percentage: number = undefined;

    @JsonProperty('Nature', NatureAdapter, true)
    private _nature: Nature = undefined;

    @JsonProperty('IsResident', Boolean, true)
    private _isResident: boolean = undefined;

    @JsonProperty('IsForeigner', BooleanInverterAdapter, true)
    private _isSpanish: boolean = undefined;

    @JsonProperty('ExistsFinalNaturalMainShareholders', Boolean, true)
    private _hasShareHolders: boolean = undefined;

    @JsonProperty('FinalMainShareHolders', [ShareHolder], true)
    private _shareHolders: ShareHolder[] = undefined;

    @JsonProperty('PBCData', PBC, true)
    private _pbc?: PBCInterface = undefined;

    private _rejected = false;

    constructor(type: ShareHolderType = ShareHolderType.SHAREHOLDER) {
        this._id = Id.fromString(uuid.v4());
        this._name = '';
        this._surname = '';
        this._type = type;
        this.nature = Nature.NATURAL;
        this.percentage = 25;
        this.isSpanish = true;
        this.isResident = true;
    }

    get id(): Id {
        return this._id;
    }

    get shareholderId(): number {
        return this._shareholderId;
    }

    set shareholderId(value: number) {
        this._shareholderId = value;
    }

    get type(): ShareHolderType {
        return this._type;
    }

    set type(value: ShareHolderType) {
        this._type = value;
    }

    get name(): string {
        return this._name;
    }

    set name(value: string) {
        this._name = value;
    }

    get surname(): string {
        return this._surname;
    }

    set surname(value: string) {
        this._surname = value;
    }

    get fullName(): string {
        if (this.isLegalPerson) {
            return this.name;
        }

        return `${this.name} ${this.surname}`;
    }

    get docType(): DocumentType {
        return this._docType;
    }

    set docType(value: DocumentType) {
        this._docType = value;
    }

    get allowedDocTypes(): DocumentType[] {
        if (this.nature === Nature.COMPANY) {
            return [
                DocumentType.CIF
            ];
        } else if (this.isSpanish && this.isResident) {
            return [
                DocumentType.DNI
            ];
        } else if (this.isSpanish && !this.isResident) {
            return [
                DocumentType.DNI,
                DocumentType.NIF,
                DocumentType.NIE_NOT_RESIDENT
            ];
        } else if (!this.isSpanish && this.isResident) {
            return [
                DocumentType.NIE,
                DocumentType.PERMISSION,
                DocumentType.PASSPORT,
                DocumentType.PASSPORT_LATIN
            ];
        }

        return [
            DocumentType.NIE_NOT_RESIDENT,
            DocumentType.PASSPORT,
            DocumentType.PASSPORT_LATIN,
            DocumentType.IDENTIFICATION_CARD_NOT_RESIDENT
        ];
    }

    get docId(): string {
        return this._docId;
    }

    set docId(value: string) {
        this._docId = value ? value.toUpperCase() : value;
    }

    get citizenship(): string {
        if (!this._citizenship && this.isSpanish) {
            this._citizenship = 'ES';
        }

        return this._citizenship;
    }

    set citizenship(value: string) {
        this._citizenship = value;
    }

    get countryOfResident(): string {
        return undefined;
    }

    get percentage(): number {
        return this._percentage;
    }

    set percentage(value: number) {
        this._percentage = value;
    }

    get nature(): Nature {
        return this._nature;
    }

    set nature(value: Nature) {
        this._nature = value;

        if (this._nature === Nature.NATURAL) {
            this._shareHolders = [];
        }

        if (this._nature === Nature.COMPANY) {
            this.isResident = true;
            this.isSpanish = true;
        }

        this.docType = this.allowedDocTypes[0];
    }

    get isLegalPerson(): boolean {
        return this.nature === Nature.COMPANY;
    }

    get isResident(): boolean {
        return this._isResident;
    }

    set isResident(value: boolean) {
        this._isResident = value;

        this.docType = this.allowedDocTypes[0];
        this.docId = undefined;
    }

    get isSpanish(): boolean {
        return this._isSpanish;
    }

    set isSpanish(value: boolean) {
        this._isSpanish = value;

        if (this._isSpanish) {
            this.citizenship = 'ES';
        } else {
            this.citizenship = undefined;
        }

        this.docType = this.allowedDocTypes[0];
        this.docId = undefined;
    }

    get birthDate(): Moment {
        return undefined;
    }

    get hasShareHolders(): boolean {
        return this._hasShareHolders;
    }

    set hasShareHolders(value: boolean) {
        this._hasShareHolders = value;
    }

    get shareHolders(): ShareHolder[] {
        if (!this._shareHolders) {
            this._shareHolders = [];
        }

        this._shareHolders.forEach(it => it.rejected = this.isRejected());
        return this._shareHolders;
    }

    get pbc(): PBCInterface {
        if (!this._pbc) {
            return new PBC();
        }

        return this._pbc;
    }

    get hasPBCPending(): boolean {
        return this.pbc.detection || this.shareHolders.some(it => it.hasPBCPending);
    }

    addShareHolder(value: ShareHolder) {
        if (!this.shareHolders.find(it => it.id.value === value.id.value)) {
            this.hasShareHolders = true;
            this.shareHolders.push(value);
        }
    }

    updateShareHolder(value: ShareHolder) {
        this.shareHolders.map((it, idx) => {
            if (it.id.value === value.id.value) {
                this.shareHolders[idx] = value;
            }
        });
    }

    removeShareHolder(value: ShareHolder) {
        this.shareHolders.map((it, idx) => {
            if (it.id.value === value.id.value) {
                this.shareHolders.splice(idx, 1);
                this.hasShareHolders = this.shareHolders.length !== 0;
            }
        });
    }

    set rejected(value: boolean) {
        this._rejected = value;
    }

    isRejected(): boolean {
        return this._rejected;
    }

    isComplete(): boolean {
        if (this.nature === Nature.NATURAL) {
            return !!this.type
                && !!this.nature
                && !!this.name
                && !!this.surname
                && !!this.docId
                && !!this.citizenship
                && !!this.percentage;
        } else {
            return !!this.type
                && !!this.nature
                && !!this.name
                && !!this.docId
                && !!this.citizenship
                && !!this.percentage
                && this.shareHolders.length > 0;
        }
    }
}
